<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';
    import { setScrollId } from '$lib/utils/setScrollId';

    import debug from 'debug';
    import { onMount } from 'svelte';

    import { LONGPRESS_TIMEOUT } from '../_utils/constants';
    import { getWidthByScreenSize } from '../_utils/getWidthByScreenSize';
    import createSliderController from '../_utils/sliderLogic';
    import Limiter from '../_wrappers/Limiter.svelte';
    import Transition from '../_wrappers/Transition.svelte';
    import Image from '../Image/Image.View.svelte';

    import type { ImageSize } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';

    const debugLog = debug('components:ImageSlider');

    export let id = '';
    export let bgColor = 'transparent';
    export let images: string[] = [];
    export let size: ImageSize = 'large';
    export let framed = false;
    export let shadowClass: string | undefined = undefined;
    export let activePillColor: string | undefined = undefined;
    export let inactivePillColor: string | undefined = undefined;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';

    const isInColumn = layout.isInColumn;

    const imageBorderRadiusClass = getBorderRadius('sliderImage');

    let offset = 0;
    let pointerDownStartTime = 0;
    let paused = false;

    // filter images with invalid URLs
    const filteredImages = images.filter((image) => {
        try {
            new URL(image);

            return true;
        } catch {
            debugLog(`Invalid image URL: ${image}`);

            return false;
        }
    });

    const { start, next, back, pause, resume, ...sliderController } =
        createSliderController(filteredImages);

    sliderController.offset.subscribe((newOffset) => {
        offset = newOffset;
    });

    sliderController.paused.subscribe((newPaused) => {
        paused = newPaused;
    });

    const handlePointerDown = () => {
        pointerDownStartTime = new Date().getTime();
        pause();
    };

    const handlePointerUp = (onClickCb: () => void) => () => {
        const pointerDownDuration = new Date().getTime() - pointerDownStartTime;

        if (pointerDownDuration > LONGPRESS_TIMEOUT) {
            resume();
        } else {
            onClickCb();
        }
    };

    const handlePointerLeave = () => {
        resume();
    };

    onMount(() => {
        start();
    });
</script>

<div
    {...setScrollId(id)}
    class="fun-w-full"
    class:fun-h-full={!isInColumn}
    style={!isInColumn ? `background-color: ${bgColor}` : ''}
>
    <Limiter>
        {#if filteredImages.length === 1}
            <div
                class={framed && !isInColumn
                    ? 'fun-p-6 sm:fun-px-12 sm:fun-py-6 md:fun-px-20 md:fun-py-10'
                    : 'fun-p-0'}
            >
                <div
                    class={cn(
                        'fun-relative fun-w-full fun-translate-y-0 fun-transform fun-overflow-hidden',
                        imageBorderRadiusClass,
                        shadowClass,
                    )}
                >
                    <Image {size} src={filteredImages[0]} fixedMaxWidth={getWidthByScreenSize()} />
                </div>
            </div>
        {:else}
            <Transition delay={transitionDelay} effect={transitionEffect}>
                <div
                    style="-webkit-user-select: none; -webkit-touch-callout: none;"
                    class={framed && !isInColumn
                        ? 'fun-p-6 sm:fun-px-12 sm:fun-py-6 md:fun-px-20 md:fun-py-10'
                        : 'fun-p-0'}
                >
                    <div
                        class={cn(
                            'fun-relative fun-w-full fun-translate-y-0 fun-transform fun-overflow-hidden',
                            imageBorderRadiusClass,
                            shadowClass,
                            {
                                'max-sm:fun-rounded-none max-sm:fun-shadow-none':
                                    !framed && !isInColumn,
                            },
                        )}
                    >
                        <button
                            aria-label={language === 'de' ? 'Vorheriges Bild' : 'Previous image'}
                            on:pointerdown={handlePointerDown}
                            on:pointerup={handlePointerUp(back)}
                            on:pointerleave={handlePointerLeave}
                            class="fun-absolute fun-bottom-0 fun-left-0 fun-top-0 fun-z-30 fun-w-1/2 fun-cursor-pointer"
                        />
                        <button
                            aria-label={language === 'de' ? 'Nächstes Bild' : 'Next image'}
                            on:pointerdown={handlePointerDown}
                            on:pointerup={handlePointerUp(next)}
                            on:pointerleave={handlePointerLeave}
                            class="fun-absolute fun-bottom-0 fun-right-0 fun-top-0 fun-z-30 fun-w-1/2 fun-cursor-pointer"
                        />
                        <div
                            class="fun-absolute fun-bottom-3 fun-left-0 fun-right-0 fun-z-20 fun-flex fun-w-full fun-justify-between fun-space-x-1 fun-px-3"
                        >
                            {#each filteredImages as image, index (`${image}-${index}`)}
                                {@const isActive = filteredImages.indexOf(image) < offset}
                                {@const isCurrent = filteredImages.indexOf(image) === offset}
                                <div
                                    class={cn(
                                        'fun-h-1 fun-w-full fun-flex-grow fun-overflow-hidden fun-rounded-sm',
                                        {
                                            'fun-bg-white': isActive && !activePillColor,
                                            'fun-bg-gray-400': !isActive && !inactivePillColor,
                                        },
                                    )}
                                    style={isActive && activePillColor
                                        ? `background-color: ${activePillColor}`
                                        : !isActive && inactivePillColor
                                        ? `background-color: ${inactivePillColor}`
                                        : ''}
                                >
                                    <div
                                        style="animation-fill-mode: forwards;{activePillColor
                                            ? `background-color: ${activePillColor}`
                                            : ''}"
                                        class={cn('fun-h-full fun-w-0', {
                                            'fun-bg-white': !activePillColor,
                                        })}
                                        class:fun-animate-fill-default={isCurrent}
                                        class:fun-pause={paused}
                                    />
                                </div>
                            {/each}
                        </div>
                        <div
                            class="fun-absolute fun-inset-x-0 fun-bottom-0 fun-z-10 fun-h-16 fun-bg-gradient-to-t fun-from-black/40 fun-to-black/0"
                        />
                        <div
                            class="fun-flex fun-transition fun-duration-700"
                            style="width: calc({filteredImages.length} * 100%); transform: translateX(calc({offset} * (-100% / {filteredImages.length})))"
                        >
                            {#each filteredImages as image, index (`${image}-loop-${index}`)}
                                <div class="fun-w-full">
                                    <Image
                                        {size}
                                        src={image}
                                        fixedMaxWidth={getWidthByScreenSize()}
                                    />
                                </div>
                            {/each}
                        </div>
                    </div>
                </div>
            </Transition>
        {/if}
    </Limiter>
</div>
